import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { useApi, useMutation } from "../hooks/useApi";
import { useNav, T, Btn, Input, Card, Spinner, ErrorMsg, MetricCard } from "../components/shared";

// ─── Types ────────────────────────────────────────────────────────────────────

type PaymentSettings = {
  stripeEnabled?: boolean;        stripePublishableKey?: string;
  stripeWebhookSecret?: string;   stripeOriginPriceId?: string;
  stripeReservePriceId?: string;  stripeVaultPriceId?: string;
  paypalEnabled?: boolean;        paypalClientId?: string;
  paypalMode?: "sandbox" | "live"; currency?: string;
};

type PublicConfig = {
  stripeEnabled: boolean;   stripePublishableKey?: string;
  paypalEnabled: boolean;   paypalClientId?: string;
  paypalMode?: string;      currency?: string;
};

// ─── Admin: Payments Settings Page ───────────────────────────────────────────

export function AdminPayments() {
  const { data: settings, loading, refetch } = useApi(() =>
    fetch("/api/payments/settings", {
      headers: { Authorization: `Bearer ${localStorage.getItem("prov_token")}` }
    }).then(r => r.json()) as Promise<PaymentSettings>, []
  );

  const [form, setForm] = useState<PaymentSettings>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const f = (key: keyof PaymentSettings) => (v: string | boolean) =>
    setForm(p => ({ ...p, [key]: v }));

  const { mutate: save, loading: saving, error } = useMutation(async () => {
    const res = await fetch("/api/payments/settings", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("prov_token")}`,
      },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error(await res.text());
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    refetch();
  });

  if (loading) return <div style={{ padding: 48 }}><Spinner /></div>;

  return (
    <div style={{ padding: "48px 52px", maxWidth: 760 }} className="fade-in">
      <div style={{ marginBottom: 36 }}>
        <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin</span>
        <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Payment Settings</h1>
        <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".82rem", color: T.mist, marginTop: 8 }}>
          Configure Stripe and PayPal. Keys are stored encrypted in the database. Your Stripe secret key should be set as a Cloudflare Worker secret, not here.
        </p>
      </div>

      {/* Currency */}
      <Card style={{ marginBottom: 2 }}>
        <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".82rem", letterSpacing: ".14em", color: T.forestDeep, marginBottom: 20 }}>General</h3>
        <Input label="Currency" type="select" value={form.currency ?? "USD"} onChange={f("currency")}
          options={[{ value: "USD", label: "USD — US Dollar" }, { value: "EUR", label: "EUR — Euro" }, { value: "GBP", label: "GBP — British Pound" }, { value: "CHF", label: "CHF — Swiss Franc" }]} style={{ maxWidth: 280 }} />
      </Card>

      {/* Stripe */}
      <Card style={{ marginBottom: 2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".82rem", letterSpacing: ".14em", color: T.forestDeep }}>Stripe</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <span style={{ fontSize: ".62rem", letterSpacing: ".16em", textTransform: "uppercase", color: form.stripeEnabled ? T.success : T.mist }}>
              {form.stripeEnabled ? "Enabled" : "Disabled"}
            </span>
            <div onClick={() => f("stripeEnabled")(!form.stripeEnabled)}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.stripeEnabled ? T.forest : T.ivoryDeep, position: "relative", cursor: "pointer", transition: "background .2s" }}>
              <div style={{ position: "absolute", top: 2, left: form.stripeEnabled ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: T.white, transition: "left .2s" }} />
            </div>
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <Input label="Publishable Key (pk_live_... or pk_test_...)" value={form.stripePublishableKey ?? ""} onChange={f("stripePublishableKey")} />
          <Input label="Webhook Secret (whsec_...)" value={form.stripeWebhookSecret ?? ""} onChange={f("stripeWebhookSecret")} />
        </div>

        <div style={{ padding: "14px", background: T.ivoryWarm, marginBottom: 16 }}>
          <p style={{ fontSize: ".62rem", letterSpacing: ".18em", textTransform: "uppercase", color: T.mist, marginBottom: 8 }}>Subscription Price IDs</p>
          <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".76rem", color: T.smoke, marginBottom: 12 }}>
            Create recurring products in your Stripe Dashboard → Products → Add product. Paste the Price ID (price_...) for each tier below.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
            <Input label="Origin ($48/mo)" value={form.stripeOriginPriceId ?? ""} onChange={f("stripeOriginPriceId")} />
            <Input label="Reserve ($120/mo)" value={form.stripeReservePriceId ?? ""} onChange={f("stripeReservePriceId")} />
            <Input label="Vault ($320/mo)" value={form.stripeVaultPriceId ?? ""} onChange={f("stripeVaultPriceId")} />
          </div>
        </div>

        <div style={{ padding: "12px 16px", background: "#eaf3de", border: `1px solid ${T.ivoryDeep}`, fontSize: ".72rem", color: T.smoke, lineHeight: 1.7 }}>
          <strong style={{ color: T.forestDeep }}>Stripe secret key</strong> — set this as a Cloudflare Worker secret, never store it here:<br />
          <code style={{ fontFamily: "'Courier New', monospace", background: T.ivoryWarm, padding: "2px 6px" }}>wrangler secret put STRIPE_SECRET_KEY</code>
        </div>
      </Card>

      {/* PayPal */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".82rem", letterSpacing: ".14em", color: T.forestDeep }}>PayPal</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <span style={{ fontSize: ".62rem", letterSpacing: ".16em", textTransform: "uppercase", color: form.paypalEnabled ? T.success : T.mist }}>
              {form.paypalEnabled ? "Enabled" : "Disabled"}
            </span>
            <div onClick={() => f("paypalEnabled")(!form.paypalEnabled)}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.paypalEnabled ? T.forest : T.ivoryDeep, position: "relative", cursor: "pointer", transition: "background .2s" }}>
              <div style={{ position: "absolute", top: 2, left: form.paypalEnabled ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: T.white, transition: "left .2s" }} />
            </div>
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <Input label="Client ID" value={form.paypalClientId ?? ""} onChange={f("paypalClientId")} />
          <Input label="Mode" type="select" value={form.paypalMode ?? "sandbox"} onChange={f("paypalMode")}
            options={[{ value: "sandbox", label: "Sandbox (testing)" }, { value: "live", label: "Live (production)" }]} />
        </div>

        <div style={{ padding: "12px 16px", background: "#eaf3de", border: `1px solid ${T.ivoryDeep}`, fontSize: ".72rem", color: T.smoke, lineHeight: 1.7 }}>
          <strong style={{ color: T.forestDeep }}>PayPal client secret</strong> — set as a Worker secret:<br />
          <code style={{ fontFamily: "'Courier New', monospace", background: T.ivoryWarm, padding: "2px 6px" }}>wrangler secret put PAYPAL_CLIENT_SECRET</code>
        </div>
      </Card>

      {error && <ErrorMsg msg={error} />}

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Btn onClick={() => save()} disabled={saving}>
          {saved ? "✓ Saved" : saving ? "Saving…" : "Save Settings"}
        </Btn>
        {saved && <span style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".8rem", color: T.mist }}>Settings updated successfully.</span>}
      </div>
    </div>
  );
}

// ─── Checkout Modal — used on lot detail & membership pages ──────────────────

export function CheckoutModal({ lotId, lotName, amountCents, onClose, onSuccess }: {
  lotId: string; lotName: string; amountCents: number;
  onClose: () => void; onSuccess: () => void;
}) {
  const [config, setConfig]     = useState<PublicConfig | null>(null);
  const [step,   setStep]       = useState<"method" | "stripe" | "paypal" | "done">("method");
  const [err,    setErr]        = useState("");
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    fetch("/api/payments/public-config").then(r => r.json()).then(setConfig);
  }, []);

  const handleStripe = async () => {
    if (!config?.stripePublishableKey) { setErr("Stripe is not configured."); return; }
    setLoading(true); setErr("");
    try {
      const res = await fetch("/api/payments/stripe/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("prov_token")}` },
        body: JSON.stringify({ lotId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // In a real integration you'd use Stripe.js here with data.clientSecret
      // For now we show the intent created successfully
      setStep("done");
      onSuccess();
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const handlePaypal = async () => {
    if (!config?.paypalClientId) { setErr("PayPal is not configured."); return; }
    setLoading(true); setErr("");
    try {
      const res = await fetch("/api/payments/paypal/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("prov_token")}` },
        body: JSON.stringify({ lotId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // In a real integration you'd redirect to PayPal or use their JS SDK
      // Order created — capture after PayPal redirect
      setStep("done");
      onSuccess();
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,29,23,.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: T.ivory, width: "100%", maxWidth: 440, padding: "40px 36px" }} className="fade-in">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: "1rem", letterSpacing: ".1em", color: T.forestDeep }}>Complete Purchase</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.4rem", color: T.mist, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "16px", background: T.ivoryWarm, marginBottom: 28 }}>
          <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>You are purchasing</p>
          <p style={{ fontFamily: "'Cormorant',serif", fontSize: "1.2rem", color: T.forestDeep }}>{lotName}</p>
          <p style={{ fontFamily: "'Cormorant',serif", fontStyle: "italic", fontSize: "1rem", color: T.brass }}>${(amountCents / 100).toFixed(2)}</p>
        </div>

        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontFamily: "'Cormorant',serif", fontSize: "2rem", color: T.forest, marginBottom: 12 }}>✓</div>
            <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".88rem", color: T.smoke }}>
              Your order has been placed. You'll receive confirmation shortly.
            </p>
          </div>
        ) : (
          <>
            {!config && <Spinner />}
            {config && !config.stripeEnabled && !config.paypalEnabled && (
              <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".84rem", color: T.mist, textAlign: "center", padding: "20px 0" }}>
                Online payments are not yet configured. Please contact us directly to complete your order.
              </p>
            )}

            {config?.stripeEnabled && (
              <button onClick={handleStripe} disabled={loading}
                style={{ width: "100%", padding: "16px", marginBottom: 10, background: T.forest, color: T.ivory, border: "none", fontFamily: "'Jost',sans-serif", fontSize: ".62rem", letterSpacing: ".2em", textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? .7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M2 7h20v10H2z" fill="#635BFF" rx="2"/><path d="M7 12h10M10 9v6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Pay with Card (Stripe)
              </button>
            )}

            {config?.paypalEnabled && (
              <button onClick={handlePaypal} disabled={loading}
                style={{ width: "100%", padding: "16px", marginBottom: 10, background: "#003087", color: "#fff", border: "none", fontFamily: "'Jost',sans-serif", fontSize: ".62rem", letterSpacing: ".2em", textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? .7 : 1 }}>
                Pay with PayPal
              </button>
            )}

            {err && <ErrorMsg msg={err} />}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Member: Subscription Manager ────────────────────────────────────────────

export function SubscriptionManager() {
  const { user } = useAuth();
  const { data: subs, loading } = useApi(() =>
    fetch("/api/payments/subscriptions", {
      headers: { Authorization: `Bearer ${localStorage.getItem("prov_token")}` }
    }).then(r => r.json()), []
  );

  const activeSub = (subs ?? []).find((s: any) => s.status === "active");

  return (
    <div style={{ maxWidth: 520 }}>
      <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".85rem", letterSpacing: ".15em", color: T.forestDeep, marginBottom: 8 }}>Subscription</h3>
      <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".82rem", color: T.mist, marginBottom: 24 }}>
        Your active membership and billing details.
      </p>

      {loading ? <Spinner /> : activeSub ? (
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              ["Tier",     activeSub.tier],
              ["Provider", activeSub.provider],
              ["Status",   activeSub.status],
              ["Amount",   `$${(activeSub.amountCents / 100).toFixed(2)}/mo`],
              ["Renews",   activeSub.currentPeriodEnd ? new Date(activeSub.currentPeriodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"],
              ["Currency", activeSub.currency ?? "USD"],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: "12px", background: T.ivoryWarm }}>
                <p style={{ fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>{k}</p>
                <p style={{ fontSize: ".82rem", color: T.ink, textTransform: "capitalize" }}>{v}</p>
              </div>
            ))}
          </div>
          {activeSub.cancelAtPeriodEnd && (
            <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".76rem", color: T.danger, marginTop: 16 }}>
              Your subscription will cancel at the end of the current period.
            </p>
          )}
        </Card>
      ) : (
        <Card>
          <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".88rem", color: T.mist, textAlign: "center", padding: "20px 0" }}>
            No active subscription found.
          </p>
        </Card>
      )}
    </div>
  );
}
